import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import FuseLoading from '@fuse/core/FuseLoading';
import SimpleDialog from '@fuse/core/SimpleDialog';
import ConfirmDeleteModalWindow from 'app/shared-components/confirmDeleteModalWindow';
import handleError from '@fuse/core/errorHandler';
import useMessage from 'src/app/hooks/useMessage';
import {
  useGetAllLeaveRequestStatusQuery,
  useDeleteLeaveRequestStatusMutation,
} from 'src/app/services/leaveRequestApi';
import AbsenceAsksStatusForm from './AbsenceAsksStatusForm';

const AbsenceAsksStatuses = () => {
  const { t, i18n } = useTranslation('translations');
  const lang = i18n.language;
  const [currentStatusId, setCurrentStatusId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const {
    data: absenceAsksStatuses = [],
    refetch,
    isLoading,
  } = useGetAllLeaveRequestStatusQuery({ lang });
  const [deleteLeaveStatus] = useDeleteLeaveRequestStatusMutation();
  const { showMessage } = useMessage();

  const showAddDialog = () => {
    setCurrentStatusId(null);
    setIsDialogOpen(true);
  };

  const showEditDialog = (id) => {
    setCurrentStatusId(id);
    setIsDialogOpen(true);
  };

  const openDeleteConfirm = (id) => {
    setSelectedId(id);
    setIsOpenDeleteModal(true);
  };

  const closeDeleteConfirm = () => {
    setIsOpenDeleteModal(false);
    setSelectedId(null);
  };

  const handleDelete = () => {
    if (selectedId == null) return;

    deleteLeaveStatus({ id: selectedId })
      .unwrap()
      .then(() => {
        showMessage({ message: t('StatusSuccessfullyDeleted'), variant: 'success' });
        closeDeleteConfirm();
        refetch();
      })
      .catch(handleError);
  };

  if (isLoading) return <FuseLoading />;

  return (
    <div style={{ display: 'grid' }}>
      <TableContainer>
        <div className="h-[40px] flex flex-row justify-between items-center dark:bg-dark">
          <h2 className="ml-5">{t('AbsenceAsksStatuses')}</h2>
          <Button
            className="mx-8"
            size="small"
            variant="outlined"
            color="secondary"
            onClick={showAddDialog}
          >
            <FuseSvgIcon size={20}>react-outline:plus</FuseSvgIcon>
            <span className="mx-8">{t('Add')}</span>
          </Button>
        </div>
        <Table style={{ overflow: 'auto' }}>
          <TableBody className="overflow-y-auto">
            {absenceAsksStatuses.map((item) => (
              <TableRow key={item.id} className="bg-white hover:bg-grey-200 dark:bg-dark">
                <TableCell>{item.name}</TableCell>
                <TableCell>
                  <div className="flex">
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      className="mr-5"
                      color="info"
                      onClick={() => showEditDialog(item.id)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      className="mr-10"
                      color="error"
                      onClick={() => openDeleteConfirm(item.id)}
                    >
                      <Delete />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <SimpleDialog
        title={currentStatusId ? t('EditAbsenceAskStatus') : t('AddAbsenceAskStatus')}
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      >
        <AbsenceAsksStatusForm
          id={currentStatusId}
          refetch={refetch}
          onClose={() => setIsDialogOpen(false)}
          isEdit={!!currentStatusId}
        />
      </SimpleDialog>
      {isOpenDeleteModal && (
        <ConfirmDeleteModalWindow
          onClose={closeDeleteConfirm}
          setIsOpenModal={setIsOpenDeleteModal}
          modalTitle={'AreYouSureToDeleteAbsenceAskStatuses'}
          deleteMessage={handleDelete}
          open={isOpenDeleteModal}
        />
      )}
    </div>
  );
};

export default AbsenceAsksStatuses;
